import membershipNames from '@/lib/membership-names'
import pubsub from '@/lib/pubsub'
import browser from '@/lib/browser'
import { httpGetJson } from '@/lib/net'
import { getProductApp } from '@/lib/redeem-utils'

import { removeUpsellRoute } from '@/services/upsell-redirect-service'
import { Vertical } from '@/types/verticals'
import type {
  Plan,
  Subscription,
  MembershipLevel,
  SubscriptionDurationUnit,
  ProductLevel,
  Offer,
  SubscriptionDurationTerm,
} from '@/types/subscriptions'
import {
  type Coupon,
  type ZipCode,
  type ProductSku,
  PurchaseAction,
} from '@/types/purchasing'
import { valueProps } from '@/lib/autosubscribe'
import type { UpsellModalProps } from '@/components/upsell-modal.vue'
import { getDifferenceBetweenDates } from '@/lib/date'
import { isActive } from '@/services/feature-flag-service'
import { captureException } from '@sentry/vue'
import {
  getHumanSku,
  getDefaultPlan,
  plansAreEquivalent,
  HUMAN_SKUS,
} from '@/lib/plans'
import marketing from '@/lib/marketing'
import { isDaily, isStaging } from '@/lib/environment'

/**
 * *(AI-generated)*
 * TYPES
 */
type PurchaseCtaProps = {
  origin?: string
  detail?: string
  action?: string
}

/**
 * Returns the base URL for the marketing site based on the environment.
 * @returns The marketing site URL as a string.
 */
const getMarketingSiteUrl = (): string => {
  if (window.Cypress) {
    return 'https://www.onxmaps.com'
  }
  if (isStaging()) {
    return 'https://www.staging.onxmaps.com'
  } else if (isDaily()) {
    return 'https://www.daily.onxmaps.com'
  }
  return 'https://www.onxmaps.com'
}

/**
 * *(AI-generated)*
 * Returns the URL for the update-payment page for the current app.
 */
export const getUpdatePaymentURL = (): string => {
  return `${getMarketingSiteUrl()}/account?view=update-payment`
}

/**
 * *(AI-generated)*
 * Returns the URL for purchasing a subscription in the app.
 */
export const getAppPurchaseURL = (
  queryString: string,
  app?: Vertical
): string => {
  const currentApp = app ?? Vertical.Fish
  const purchaseUrl = `${getMarketingSiteUrl()}/purchase/${currentApp}/membership`

  return queryString ? `${purchaseUrl}?${queryString}` : purchaseUrl
}

/**
 * Gets all US states
 *    [
 *      {abbr: 'ak', name: 'Alaska'},
 *      {abbr: 'al', name: 'Alabama'},
 *      {abbr: 'az', name: 'Arizona'},
 *      ...
 *    }
 */
export function getHuntStates() {
  const statesRaw = membershipNames.getStateNames()
  return Object.keys(statesRaw)
    .map((key) => ({ abbr: key, name: statesRaw[key] }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Gets all additional hunt regions
 *
 * @return all states in the form:
 *    [
 *      {abbr: 'can', name: 'Canada'},
 *      ...
 *    }
 */
export function getHuntRegions() {
  const regionsRaw = membershipNames.getRegionNames()
  return Object.keys(regionsRaw)
    .map((key) => ({
      abbr: key,
      name: regionsRaw[key],
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Returns a state given an abbreviation
 *
 * @param abbr - state abbreviation (e.g. 'mt', 'co')
 * @return the name (e.g. 'Montana', 'Colorado')
 */
export function getStateName(abbr: string) {
  return membershipNames.getStateNames()[abbr]
}

/**
 * Gets the smart link for an app.
 *
 * @param page - The page identifier.
 * @returns The smart link for the app as a string.
 */
export function getSmartLink(page?: string): string {
  // Remove undefined params
  const params = new URLSearchParams(
    JSON.parse(JSON.stringify({ site_id: page }))
  )
  // FUTURE
  return `https://onxbackcountry.smart.link/houfq3yjhyb0a?${params.toString()}`
}

type subscriptions = { [key in Vertical]: Subscription }

/**
 * Get a subscription for a customer
 * FUTURE: Use the subscription service
 *
 * @param app - The app name (e.g., 'hunt', 'offroad', 'fish').
 * @returns The user's subscription for that app as an object.
 */
export async function getSubscription(app: Vertical): Promise<Subscription> {
  const allSubs = await httpGetJson<subscriptions>('/v2/subscriptions')
  return allSubs[app]
}

/**
 * Navigate user to purchase flow based on split
 *
 *  @param marketingProps - page information for the marketing event
 *  @param queryString - the query parameter to use in the purchase url
 *  @returns the purchase url to route to
 */
export function goToPurchase(
  marketingProps: PurchaseCtaProps,
  queryString = '',
  app?: Vertical
) {
  const url = getAppPurchaseURL(queryString, app)

  if (Object.keys(marketingProps).length) {
    marketing.logEvent('purchase_cta', marketingProps)
  }

  return browser.go(url)
}

/**
 * Display upsell modal based for basic users.
 *
 *  @param marketingProps - page information for the marketing event
 *  @param queryString - the query parameter to use in the purchase url
 */
export function displayUpsellModal(
  marketingProps: PurchaseCtaProps,
  modalProps?: Partial<UpsellModalProps>,
  queryString = ''
) {
  pubsub.publish('show_upsell_modal', { modalProps })

  const handlePurchase = () => {
    goToPurchase(marketingProps, queryString)
  }

  const handleModalClose = () => {
    pubsub.unsubscribe('close-upsell-modal', handleModalClose)
    pubsub.unsubscribe('upsell-modal-purchase', handlePurchase)
    removeUpsellRoute()
    marketing.logEvent('purchase_cta_upsell_dismissed')
  }

  //routes to purchase flow by listening to click event
  pubsub.subscribe('upsell-modal-purchase', handlePurchase)

  // closes modal by listening to click event
  pubsub.subscribe('close-upsell-modal', handleModalClose)
}

/**
 * Display promo offer modal.
 *
 *  @param offer - the offer to display via the modal
 */
export async function displayPromoOfferModal(offer: Offer) {
  const coupon = await validateCoupon(offer.offerTag, Vertical.Fish)
  // Check if promo code included in offer is valid before we show it to the user
  if (
    couponIsValidForProduct(coupon as Coupon, offer.sku as ProductSku) ||
    isActive('debug_promotional_offers')
  ) {
    // publish pubsub message that will trigger modal to show
    pubsub.publish('show_promo_offer_modal', { offer, coupon })
  }
  if (!coupon.valid) {
    // Promo code included in offer wasn't valid!
    captureException(new Error(`Invalid offer received`), {
      tags: { feature: 'promo-offer' },
      extra: { offer },
    })
  }
}

/**
 * Get the sale price of a product.
 *
 * @param productPrice - The product's price to apply the coupon to.
 * @param coupon - The coupon to use to apply the discounted price.
 * @returns The formatted price string (e.g., '$15.99').
 */
export function getSalePrice(productPrice: number, coupon: Coupon): string {
  const priceInteger = getSalePriceInteger(productPrice, coupon)
  return `$${(priceInteger / 100).toFixed(2)}`
}

/**
 * Get the sale price of a product as an integer.
 *
 * @param productPrice - The product's price to apply the coupon to.
 * @param coupon - The coupon to use to apply the discounted price.
 * @returns The price in cents (e.g., 1599).
 */
export function getSalePriceInteger(
  productPrice: number,
  coupon: Coupon
): number {
  const basePrice = parseFloat((productPrice / 100).toFixed(2))

  if (!coupon || !coupon.valid) {
    return Math.round(productPrice)
  }

  const amount_off = (coupon.amount_off ?? 0) / 100 || 0
  const percentOffInteger = coupon.percent_off || 0
  const percent_off = (100 - percentOffInteger) / 100
  const price = Math.max(
    Math.round((basePrice * percent_off - amount_off + Number.EPSILON) * 100),
    0
  )
  return price
}

/**
 * Get the price at which a product renews at
 *
 * @param product - the product to apply the sale price to
 * @param coupon - the coupon to use to apply the sale price
 * @return the price e.g. '$15.99'
 */
export function getSaleRenewPrice(product: Plan, coupon: Coupon): string {
  const formattedBasePrice = formatPrice(product.price)
  const basePrice = parseFloat((product.price / 100).toFixed(2))

  // A coupon which does not discount renewals
  if (!coupon || !coupon.valid || coupon.duration === 'once') {
    return formattedBasePrice
  }

  const amount_off = (coupon.amount_off ?? 0) / 100 || 0
  const percent_off = coupon.percent_off ? (100 - coupon.percent_off) / 100 : 1
  const price = Math.max(
    Math.round((basePrice * percent_off - amount_off + Number.EPSILON) * 100) /
      100,
    0
  )
  const formattedPrice = `$${price.toFixed(2)}`

  // A coupon that discounts all renewals
  if (coupon.duration === 'forever') {
    return formattedPrice
  }

  // Else we have a coupon which discounts for a given number of months
  const nMonths = coupon.duration_in_months

  if (product.term?.unit === 'MONTH') {
    return `${formattedPrice} for ${nMonths} months then ${formattedBasePrice}`
  }

  // Coupons are applied to each charge/renewal within the duration_in_months
  // Ex. a coupon with duration_in_months = 13 will apply to the initial purchase and the first renewal
  const remainder = (coupon.duration_in_months ?? 0) % 12
  const duration =
    remainder !== 0
      ? Math.floor((coupon.duration_in_months ?? 0) / 12) + 1
      : Math.floor((coupon.duration_in_months ?? 0) / 12)

  // This duration = 0 case should be unreachable unless the api allows creating coupons with duration_in_months = 0
  if (duration === 0) {
    return formattedBasePrice
  }

  if (duration === 1) {
    return `${formattedPrice} once then ${formattedBasePrice}`
  }

  if (duration === 2) {
    return `${formattedPrice} twice then ${formattedBasePrice}`
  }

  return `${formattedPrice} ${duration} times then ${formattedBasePrice}`
}

/**
 * All states including DC
 *
 * @return all states in the form:
 *    [
 *      {abbr: 'ak', name: 'Alaska'},
 *      {abbr: 'al', name: 'Alabama'},
 *      {abbr: 'az', name: 'Arizona'},
 *      ...
 *    }
 */
export function getBillingStates() {
  const statesRaw = membershipNames.getStateNames()
  statesRaw['dc'] = 'District of Columbia'
  return Object.keys(statesRaw)
    .map((key) => ({ abbr: key, name: statesRaw[key] }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/**
 * Get the description of a coupon based on its percent_off, amount_off, and duration.
 *
 * @param coupon - The coupon object.
 * @param product - The product to determine monthly vs. yearly.
 * @returns A description of the coupon as a string.
 */
export function getCouponDescription(coupon: Coupon, product?: Plan): string {
  let discount: string

  if (coupon.percent_off) {
    discount = `${coupon.percent_off}%`
  } else if (coupon.amount_off) {
    discount = `$${coupon.amount_off / 100}`
  } else {
    // If no percent or amount off then return an empty description
    return ''
  }

  if (!product || !product.term?.unit) {
    return `${discount} off`
  }

  if (coupon.duration === 'once') {
    return `${discount} off your first ${product.term?.unit?.toLowerCase()}`
  }

  if (coupon.duration === 'forever') {
    return `${discount} off every ${product.term?.unit?.toLowerCase()}`
  }

  if (coupon.duration === 'repeating') {
    if (product.term?.unit === 'MONTH') {
      return `${discount} off for ${coupon.duration_in_months} months`
    }

    const duration = Math.floor((coupon.duration_in_months ?? 0) / 12)
    if (duration === 0) {
      return `${discount} off your first year`
    }
    if (duration === 1) {
      return `${discount} off for 2 years`
    }

    return `${discount} off for ${duration + 1} years`
  }

  return ''
}

/**
 * Get a coupon's description as it applies to different membership levels.
 *
 * @param coupon - a coupon object
 * @param app - the app to calculate the description for
 * @returns A description of the coupon as a string.
 */
export function getCouponDescriptionWithMembership(
  coupon: Coupon,
  app: Vertical
): string {
  const description = getCouponDescription(coupon)
  const p = coupon.products
  if (!p) {
    return description
  }

  // Backcountry
  if (app === 'backcountry') {
    return description
  }

  // Offroad
  if (app === 'offroad') {
    if (
      p.includes(HUMAN_SKUS.OFFROAD_PREMIUM_YEAR) &&
      p.includes(HUMAN_SKUS.OFFROAD_ELITE_YEAR)
    ) {
      return description
    } else if (p.includes(HUMAN_SKUS.OFFROAD_PREMIUM_YEAR)) {
      return `${description} Premium`
    } else if (p.includes(HUMAN_SKUS.OFFROAD_ELITE_YEAR)) {
      return `${description} Elite`
    }

    return description
  }

  // Hunt
  let state = 0

  if (p.includes(HUMAN_SKUS.HUNT_PREMIUM_YEAR)) {
    state += 1
  }

  if (p.includes(HUMAN_SKUS.HUNT_ELITE_YEAR)) {
    state += 2
  }

  if (p.includes(HUMAN_SKUS.HUNT_ELITE_MONTH)) {
    state += 4
  }

  switch (state) {
    case 1:
      return `${description} Premium`
    case 2:
      return `${description} Elite Yearly`
    case 3:
      return `${description} Premium and Elite Yearly`
    case 4:
      return `${description} Elite Monthly`
    case 5:
      return `${description} Premium and Elite Monthly`
    case 6:
      return `${description} Elite`
    case 7:
      return description
  }

  return description
}

/**
 * Validate a coupon
 *
 * @param couponCode in url or promo code input
 * @return coupon object
 */
export async function validateCoupon(
  couponCode: string,
  app: Vertical
): Promise<Coupon | { id: string; valid: false }> {
  let coupon

  try {
    const code = couponCode.toLowerCase()
    coupon = (await httpGetJson(`/v1/coupons/${code}`)) as Coupon
  } catch {
    return {
      id: couponCode,
      valid: false,
    }
  }

  if (coupon.products) {
    let appliesToApp = false

    coupon.products.forEach((product) => {
      if (getProductApp(product) === app) {
        appliesToApp = true
      }
    })

    coupon.valid = appliesToApp
  }

  return coupon
}

/**
 * Determines if the user has an existing subscription for a product
 *
 * @param app - The app name (e.g., 'hunt', 'offroad', 'fish').
 * @param product - The product to compare the existing subscription to.
 * @return subscription type and expiration date or an empty object
 */
export async function existingSubscription(app: Vertical, product: Plan) {
  const sub = (await getSubscription(app)) as Subscription

  if (
    sub.type === 'redemption' &&
    sub.expires_at &&
    sub.membership_level === product.level.toLowerCase()
  ) {
    const expireDate = new Date(sub.expires_at)
    return {
      type: 'redemption',
      expireDate: Intl.DateTimeFormat('en-US').format(expireDate),
    }
  }

  return {}
}

/**
 * Determine if a coupon is valid for a certain product.
 *
 * @param coupon - The coupon object.
 * @param productSku - The product's sku.
 * @returns Whether the coupon is valid for the product (boolean).
 */
export function couponIsValidForProduct(
  coupon: Coupon,
  productSku: ProductSku
): boolean {
  return (
    coupon.valid && (!coupon.products || coupon.products.includes(productSku))
  )
}

/**
 * Get the error message from an xgps response
 *
 * @param err - The error response object.
 * @returns The error message as a string.
 */
export function getErrorMessage(
  err: {
    error_id?: number
    errors?: string
    [key: string]: unknown
  } | null
): string | string[] {
  const defaultMessage = 'There was an error while completing your purchase'
  if (!err) {
    return defaultMessage
  }

  if (err.error_id === 10) {
    return 'Our records indicate you are already subscribed to this plan.'
  }

  if (typeof err.errors === 'string' && err.errors.length > 0) {
    return err.errors
  }

  return defaultMessage
}

/**
 * Validates that a zip code is not empty, only contains numbers, and is no more than 5 digits
 * Sets the zip.valid and zip.errorMessage accordingly.
 *
 * @param zip - The zip code to validate
 */
export function validateZip(zip: ZipCode) {
  const value = zip.value.trim()

  // Regex for US ZIP (12345 or 12345-6789)
  const usRegex = /^[0-9]{5}(?:-[0-9]{4})?$/

  // Regex for Canadian postal code (A1A 1A1 or A1A1A1)
  const caRegex =
    /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i

  // valid if it matches *either* one
  const isValid = usRegex.test(value) || caRegex.test(value)

  if (!value) {
    zip.errorMessage = 'This field is required'
    zip.valid = false
  } else if (!isValid) {
    zip.errorMessage = 'Invalid postal/ZIP code'
    zip.valid = false
  } else {
    zip.errorMessage = ''
    zip.valid = true
  }
}

/**
 * converts price from number to currency (ex. 9999 to '$99.99')
 *
 * @param price - The price to format as a string.
 * @returns The formatted currency string.
 */
export function formatPrice(price: number): string {
  // convert price from number to currency (ex. 9999 to '$99.99')
  const convertedPrice = price.toString()
  const dollars = convertedPrice.slice(0, convertedPrice.length - 2)
  const cents = convertedPrice.slice(-2)
  return `$${dollars}.${cents}`
}

/**
 * capitalizes first character of string
 *
 * @param value - The string to format.
 * @returns The formatted string.
 */
export function capitalize(value: string): string {
  const lowerCaseValue = value.toLowerCase()
  return `${lowerCaseValue?.charAt(0)?.toUpperCase() ?? ''}${
    lowerCaseValue?.slice(1) ?? ''
  }`
}

/**
 * *(AI-generated)*
 * Returns the display title for a given product.
 */
export const getProductTitle = (product: {
  name?: string
  level: ProductLevel
}) => {
  const productTitleMap = {
    PREMIUM: 'Premium',
    PREMIUM_MULTI_STATE: 'Premium With Two States',
    ELITE: 'Elite',
  }

  return product.name ?? productTitleMap[product.level] ?? ''
}

/**
 * Formats a product's term value.
 *
 * @param term - The product's term value.
 * @param type - The format type ('short', 'capitalize', or default).
 * @returns The formatted term as a string.
 */
export function formatTerm(
  term: SubscriptionDurationUnit,
  type?: 'short' | 'capitalize'
): string {
  const lowercase = {
    MONTH: 'monthly',
    YEAR: 'yearly',
  }
  const shortened = {
    MONTH: 'mo',
    YEAR: 'yr',
  }
  const capitalized = {
    MONTH: 'Monthly',
    YEAR: 'Yearly',
  }

  if (type === 'short') {
    return shortened[term]
  }

  if (type === 'capitalize') {
    return capitalized[term]
  }

  return lowercase[term]
}

/**
 * Pluralizes a subscription term unit based on term.amount
 * @returns term.unit with an s potentially added
 */
export function pluralizedTermUnit(term: SubscriptionDurationTerm): string {
  if (!term || !term.amount || !term.unit) {
    return ''
  }
  const pluralizedString = term.amount > 1 ? `${term.unit}S` : term.unit
  return pluralizedString.toLowerCase()
}

/**
 * Filters products by level.
 *
 * @param products - The product array.
 * @param levels - Multiple levels passed as separate arguments, collected into an array.
 * @returns An array of products matching the specified levels.
 */
export function filterProducts(
  products: Plan[] = [],
  ...levels: MembershipLevel[]
): Plan[] {
  return products.filter((product) => levels.includes(product.level))
}

/**
 * Checks if the supplied products list contains multiple products with matching subscription levels, but different durations (terms).
 * Used to determine if the user must choose a billing frequency (monthly or yearly)
 *
 * @param products - The array of products to search
 * @param selectedLevel - An optional level to check billing options for
 */
export function billingOptionsAvailable(
  products: Plan[],
  selectedLevel?: ProductLevel
) {
  const levels = selectedLevel
    ? [selectedLevel]
    : ['ELITE', 'PREMIUM', 'PREMIUM_MULTI_STATE']
  const result = levels.find((level) => {
    return (
      products?.some((product) => {
        return product.level === level && product.term?.unit === 'YEAR'
      }) &&
      products?.some((product) => {
        return product.level === level && product.term?.unit === 'MONTH'
      })
    )
  })
  return !!result
}

/**
 * Checks if the user must select a state for a product of the supplied level
 *
 * @param level - The level of the users currently selected product
 */
export function mustChooseState() {
  return false
}

/**
 * Gets product value props and icons based on the app and product level.
 *
 * @param app - The app (e.g., 'hunt', 'offroad').
 * @param level - The product level (e.g., 'ELITE', 'PREMIUM', 'PREMIUM_MULTI_STATE').
 * @returns An array of value props and icons.
 */
export function getAutosubscribeValueProps(
  app: Vertical,
  level: MembershipLevel
) {
  return valueProps[app][level]
}

/**
 * Attempts to creates a Plan using a Subscription returned from v2/subscriptions
 * Can be used to partially construct a Plan that's not yet in client side defaults
 * @param sub A subscription retrieved from v2/subscriptions
 * @returns A Plan populated as much as possible or undefined
 */
export function constructPlanFromV2Sub(sub: Subscription): Plan | undefined {
  if (
    !sub.membership_level ||
    ['trial', 'basic', 'employee'].includes(sub.membership_level) ||
    !sub.started_at ||
    !sub.expires_at ||
    !sub.product_code
  ) {
    return undefined
  }
  const dateDiff = getDifferenceBetweenDates(
    sub.started_at,
    sub.expires_at
  ) as { days: number }
  const unit = dateDiff.days > 31 ? 'YEAR' : 'MONTH'
  return {
    sku: sub.product_code,
    name: `${capitalize('fish')} ${capitalize(sub.membership_level)}`,
    price: 0,
    level: sub.membership_level.toUpperCase() as ProductLevel,
    description: [''],
    trialTerm: null,
    term: { amount: 1, unit: unit },
  }
}

/**
 * Calculates the action (upgrade downgrade etc.) the user is taking based on their current subscription and the product they are trying to purchase
 * @param pastDue - Is the user subscription already past due?
 * @param newProduct - The product the user is trying to purchase
 * @param userSubscription - The user's current subscription
 * @returns A string representing the action the user is taking. Ex 'upgrade', 'downgrade', etc.
 */
export function calculateAction(
  pastDue: boolean,
  newProduct: Plan | null,
  userSubscription: Subscription | null
): PurchaseAction {
  if (!newProduct || !userSubscription) {
    return PurchaseAction.Error
  }

  if (pastDue) {
    return PurchaseAction.PastDue
  }

  if (
    !userSubscription.membership_level ||
    ['trial', 'basic'].includes(userSubscription.membership_level)
  ) {
    return PurchaseAction.New
  }

  if (userSubscription.canceled_at !== null) {
    return PurchaseAction.Canceled
  }

  if (
    userSubscription.type === PurchaseAction.Apple ||
    userSubscription.type === PurchaseAction.Google ||
    userSubscription.type === PurchaseAction.Employee ||
    userSubscription.type === PurchaseAction.Redemption
  ) {
    return userSubscription.type
  }

  // Get default plan if one exists, if not construct one from the v2/subscriptions response
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const defaultPlan = getDefaultPlan(userSubscription.product_code!)
  const currentProduct = defaultPlan
    ? defaultPlan
    : constructPlanFromV2Sub(userSubscription)

  if (plansAreEquivalent(currentProduct, newProduct)) {
    return PurchaseAction.SameProduct
  }

  const currentMemLevel = userSubscription.membership_level
  const newMemLevel = newProduct.level.toLowerCase()
  if (
    ((newMemLevel === 'premium' || newMemLevel === 'premium_multi_state') &&
      currentMemLevel === 'elite') ||
    (newMemLevel === 'premium' && currentMemLevel === 'premium_multi_state')
  ) {
    const newProductHumanSku = getHumanSku(newProduct.sku) ?? newProduct.sku
    // In some cases they've already downgraded so they might be trying to buy a product
    // they've already downgraded to.
    if (
      newProductHumanSku === userSubscription.renewal_product ||
      newProduct?.sku === userSubscription.renewal_product
    ) {
      return PurchaseAction.AlreadyDowngraded
    }

    return PurchaseAction.Downgrade
  }

  return PurchaseAction.Upgrade
}
